import { useState, useCallback } from 'react';
import type { CheckoutUser, CheckoutAddress, PaymentMethods, PendingOrder } from '../../../types/checkout';

interface EmailOtpSectionProps {
  onSuccess: (user: CheckoutUser, addresses: CheckoutAddress[], paymentMethods: PaymentMethods, accRecOverLimit: boolean, pendingOrders?: PendingOrder[]) => void;
  sendOtp: (email: string) => Promise<{ success: boolean; message: string }>;
  verifyOtp: (email: string, otp: string) => Promise<{
    success: boolean; message: string;
    user?: CheckoutUser; addresses?: CheckoutAddress[];
    paymentMethods?: PaymentMethods; accRecOverLimit?: boolean;
    pendingOrders?: PendingOrder[];
  }>;
  isLoading: boolean;
  initialEmail?: string;
  isLoggedIn?: boolean;
}

type Stage = 'email' | 'otp';

export function EmailOtpSection({
  onSuccess,
  sendOtp,
  verifyOtp,
  isLoading,
  initialEmail = '',
  isLoggedIn = false,
}: EmailOtpSectionProps) {
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (isLoggedIn && initialEmail) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-500">Sesion iniciada como:</p>
        <div className="flex items-center gap-2.5 rounded-lg border border-green-300 bg-green-50 px-4 py-3.5 text-base text-gray-800">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5cb85c" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{initialEmail}</span>
        </div>
      </div>
    );
  }

  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!email.trim()) {
      setError('Ingresa tu email');
      return;
    }

    const result = await sendOtp(email.trim().toLowerCase());

    if (result.success) {
      setStage('otp');
      setSuccessMessage(result.message);
    } else {
      setError(result.message);
    }
  }, [email, sendOtp]);

  const handleOtpSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!otp.trim() || otp.length !== 6) {
      setError('Ingresa el codigo de 6 digitos');
      return;
    }

    const result = await verifyOtp(email.trim().toLowerCase(), otp.trim());

    if (result.success && result.user && result.paymentMethods) {
      onSuccess(result.user, result.addresses || [], result.paymentMethods, result.accRecOverLimit || false, result.pendingOrders);
    } else {
      setError(result.message);
    }
  }, [email, otp, verifyOtp, onSuccess]);

  const handleResendOtp = useCallback(async () => {
    setError(null);
    setSuccessMessage(null);
    setOtp('');

    const result = await sendOtp(email.trim().toLowerCase());

    if (result.success) {
      setSuccessMessage('Nuevo codigo enviado');
    } else {
      setError(result.message);
    }
  }, [email, sendOtp]);

  return (
    <div>
      {stage === 'email' ? (
        <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-500">
            Ingresa tu email para recibir un codigo de verificacion
          </p>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="checkout-email" className="text-sm font-medium text-gray-800">Email</label>
            <input
              type="email"
              id="checkout-email"
              className="rounded-lg border border-gray-300 px-3.5 py-3 text-base transition-colors focus:border-green-500 focus:outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              autoComplete="email"
              autoFocus
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</div>
          )}

          <button
            type="submit"
            className="rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            disabled={isLoading || !email.trim()}
          >
            {isLoading ? 'Enviando...' : 'Enviar codigo'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit} className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-500">
            Enviamos un codigo a <strong>{email}</strong>
          </p>

          {successMessage && (
            <div className="rounded-lg border border-green-300 bg-green-50 px-3.5 py-2.5 text-sm text-green-800">{successMessage}</div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="checkout-otp" className="text-sm font-medium text-gray-800">Codigo de verificacion</label>
            <input
              type="text"
              id="checkout-otp"
              className="rounded-lg border border-gray-300 p-4 text-center font-mono text-2xl tracking-[8px] transition-colors focus:border-green-500 focus:outline-none"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</div>
          )}

          <button
            type="submit"
            className="rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            disabled={isLoading || otp.length !== 6}
          >
            {isLoading ? 'Verificando...' : 'Verificar codigo'}
          </button>

          <div className="flex justify-center gap-6">
            <button
              type="button"
              className="border-none bg-transparent text-sm text-green-500 underline hover:text-green-600 disabled:cursor-not-allowed disabled:text-gray-400"
              onClick={handleResendOtp}
              disabled={isLoading}
            >
              Reenviar codigo
            </button>
            <button
              type="button"
              className="border-none bg-transparent text-sm text-green-500 underline hover:text-green-600 disabled:cursor-not-allowed disabled:text-gray-400"
              onClick={() => { setStage('email'); setOtp(''); setError(null); setSuccessMessage(null); }}
              disabled={isLoading}
            >
              Cambiar email
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
